import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, MessageSquare, AlertCircle, Lock, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function AdvancedAnalytics({ canAccess }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE}/api/interactions/analytics/advanced?days=30`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
        });
        if (!res.ok) throw new Error(`Failed (${res.status})`);
        const json = await res.json();
        if (!cancelled) setData(json.data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canAccess]);

  if (!canAccess) {
    return (
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-8">
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-[14px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Advanced Analytics</h3>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-5 max-w-md mx-auto">
            Top questions, daily conversation trends, and unanswered queries — available on the Pro plan.
          </p>
          <Link
            to="/dashboard/billing"
            className="inline-block px-5 py-2 rounded-full bg-gray-900 text-white dark:bg-white dark:text-black font-medium text-sm hover:bg-black dark:hover:bg-gray-200 transition-colors"
          >
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-8">
        <p className="text-sm text-gray-500 dark:text-gray-500">Couldn't load advanced analytics. {error || ''}</p>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...data.dailyVolume.map((d) => d.count));

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Top Questions */}
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Top questions</h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-500 font-light mb-4">Last {data.rangeDays} days · ranked by frequency</p>
        {data.topQuestions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-500 py-4">No data yet — once visitors start chatting, their most-asked questions will appear here.</p>
        ) : (
          <ul className="space-y-2.5">
            {data.topQuestions.map((q, i) => (
              <li key={i} className="flex items-start justify-between gap-3 py-1.5 border-b last:border-0 border-gray-100 dark:border-white/[0.04]">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="text-xs font-medium tabular-nums text-gray-400 dark:text-gray-600 mt-0.5 w-5 shrink-0">{i + 1}.</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{q.question}</span>
                </div>
                <span className="text-xs font-medium tabular-nums text-gray-900 dark:text-white shrink-0">{q.count}×</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Daily volume */}
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Daily conversation volume</h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-500 font-light mb-5">Last {data.rangeDays} days · {data.totalInteractions} total interactions · {data.uniqueSessions} unique conversations</p>
        <div className="flex items-end gap-0.5 h-24">
          {data.dailyVolume.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
              <div
                className="w-full bg-gray-900 dark:bg-white rounded-t transition-all"
                style={{ height: `${Math.max((d.count / maxDaily) * 100, d.count > 0 ? 4 : 0)}%`, minWidth: '2px' }}
                title={`${d.date}: ${d.count}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-600 mt-2 font-light">
          <span>{data.dailyVolume[0]?.date}</span>
          <span>{data.dailyVolume[data.dailyVolume.length - 1]?.date}</span>
        </div>
      </div>

      {/* Unanswered */}
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-6 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="w-4 h-4 text-amber-500" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Unanswered questions</h3>
          <span className="text-xs font-medium ml-1 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400">{data.unanswered.count}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-500 font-light mb-4">Times your chatbot couldn't help — adding these topics to your knowledge base will close the gap.</p>
        {data.unanswered.samples.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-500 py-3">No unanswered questions in the last {data.rangeDays} days. Your knowledge base is covering everything.</p>
        ) : (
          <ul className="space-y-3">
            {data.unanswered.samples.map((s, i) => (
              <li key={i} className="text-sm">
                <div className="text-gray-700 dark:text-gray-300 font-medium truncate">"{s.question}"</div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5 line-clamp-2">{s.response}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default AdvancedAnalytics;
